import { useEffect, useState } from 'react'
import { hora } from '../../formato'
import type { EstadoFonte } from '../../api'
import type { RespostaFontes } from './cadastroAdmin'

/**
 * Aba Fontes — é por ela que se descobre, no meio do evento, que o número parou
 * de subir.
 *
 * Os três estados do contrato têm APARÊNCIA DISTINTA porque a ação de quem
 * opera é diferente: `indisponivel` é fonte que já funcionou e caiu (alguém vai
 * atrás da conexão); `pendente` é fonte que nunca foi configurada (o SQL do BI
 * que não chegou, a credencial do Linx que não veio) — e aí não há nada a
 * reiniciar.
 */

type Aparencia = { cor: string; selo: string; semRecarga: boolean }

const APARENCIA: Record<EstadoFonte, Aparencia> = {
  no_ar: { cor: 'var(--meta)', selo: 'No ar', semRecarga: false },
  indisponivel: { cor: 'var(--critico)', selo: 'Fora do ar', semRecarga: false },
  /* Recarregar fonte que nunca foi configurada não tem o que fazer. */
  pendente: { cor: 'var(--atencao)', selo: 'Pendente', semRecarga: true },
}

type Props = {
  dados: RespostaFontes | null
  /** `null` pede todas as fontes. */
  aoRecarregar: (fonte: string | null) => void
  recarregando: string | null
  aviso: string | null
  erro: string | null
  /** As datas vão JUNTO: ligar com a janela que está na tela é uma decisão só. */
  aoAlternarFiltroFaturamento: (ligado: boolean, de: string, ate: string) => void
  alternandoFiltro: boolean
  erroFiltro: string | null
}

/** `2026-01-01` → `01/01/2026`. Data ISO na tela é data para ninguém ler. */
function dataBr(iso: string | null): string {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ''))
  return partes === null ? '—' : `${partes[3]}/${partes[2]}/${partes[1]}`
}

/** `há 12 s` / `há 4 min` — a idade informa mais que a hora cheia. */
function idadeDe(lidoEm: string | null, agora: number): string {
  if (!lidoEm) return 'nunca coletado'
  const instante = Date.parse(lidoEm)
  if (Number.isNaN(instante)) return 'hora não reconhecida'
  const segundos = Math.max(0, Math.round((agora - instante) / 1000))
  if (segundos < 90) return `há ${segundos} s`
  if (segundos < 5400) return `há ${Math.round(segundos / 60)} min`
  return hora(instante)
}

/** De quanto em quanto tempo a idade na tela é recalculada, em ms. */
const BATIDA_MS = 5_000

/**
 * O relógio da aba, próprio.
 *
 * A idade NÃO pode depender da consulta ao servidor: quando ele está fora do ar
 * a consulta só reescreve a mesma mensagem de erro, o React descarta a
 * repintura e a idade congelaria no último valor — exatamente no momento em que
 * ela é a informação que importa. O laço morre com o componente, e o componente
 * só existe enquanto a aba está aberta.
 */
function usarRelogio(): number {
  const [agora, setAgora] = useState(() => Date.now())
  useEffect(() => {
    const laco = setInterval(() => setAgora(Date.now()), BATIDA_MS)
    return () => clearInterval(laco)
  }, [])
  return agora
}

export function AbaFontes({
  dados, aoRecarregar, recarregando, aviso, erro,
  aoAlternarFiltroFaturamento, alternandoFiltro, erroFiltro,
}: Props) {
  const motivos = Object.entries(dados?.rejeicoesPorMotivo ?? {})
  const agora = usarRelogio()
  const filtro = dados?.filtroFaturamento ?? null

  /* Rascunho das datas, separado do que veio do servidor: a aba se recarrega
     sozinha a cada 10 s, e sem rascunho a resposta da consulta sobrescreveria o
     que está sendo digitado — no meio da digitação, o que é pior que não ter o
     campo. O `useEffect` só adota o valor do servidor quando ele MUDA, não a
     cada repintura. */
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')
  useEffect(() => { setDe(filtro?.de ?? '') }, [filtro?.de])
  useEffect(() => { setAte(filtro?.ate ?? '') }, [filtro?.ate])

  const alterouDatas = filtro !== null && (de !== (filtro.de ?? '') || ate !== (filtro.ate ?? ''))
  // Janela invertida é erro de digitação: barra aqui, em vez de ir ao servidor.
  const invertida = de !== '' && ate !== '' && ate < de
  const podeSalvar = !alternandoFiltro && !invertida && de !== '' && ate !== ''

  return (
    <section>
      {/* O interruptor vem ANTES da lista de fontes, e não no fim da aba: quando
          o número está baixo com todas as fontes no ar, é ele a explicação — e
          procurá-lo embaixo de oito linhas de fonte custa tempo que, no meio do
          evento, não existe. */}
      <h2>Janela de medição</h2>
      <p className="ajuda">
        O que conta como venda no telão. A janela de emissão é cadastrada por divisão;
        o filtro por data de faturamento é um interruptor só, que vale para todas.
      </p>

      <div className="painel-admin">
        {filtro === null ? (
          <div className="ajuda">
            {erro
              ? 'Não foi possível ler o filtro de faturamento.'
              : 'Lendo o filtro de faturamento…'}
          </div>
        ) : (
          <div className="fonte">
            <span
              className="ponto"
              style={{ background: filtro.ligado ? 'var(--atencao)' : 'var(--meta)' }}
            />
            <div>
              <div className="fonte-nome">
                Filtro por data de faturamento
                <span className="fonte-qualificador">
                  {filtro.ligado ? ` · de ${dataBr(filtro.de)} a ${dataBr(filtro.ate)}` : ' · sem recorte'}
                </span>
              </div>
              <div className="fonte-detalhe">
                {filtro.ligado
                  ? 'Ligado: entra só o pedido com faturamento previsto dentro da janela. ' +
                    'Medido sobre 2025, isso descarta 94,7 % das linhas e zera a Vila Flor, ' +
                    'cuja data de faturamento é nula em 99,89 % dos pedidos.'
                  : 'Desligado: todo pedido emitido na janela conta, com ou sem data de ' +
                    'faturamento. É o padrão desde a medição da P-16.'}
              </div>
            </div>
            <div className="fonte-estado">
              <div
                className="selo"
                style={{ color: filtro.ligado ? 'var(--atencao)' : 'var(--meta)' }}
              >
                {filtro.ligado ? 'Ligado' : 'Desligado'}
              </div>
              {/* Quem muda isto fica olhando o telão esperando o número pular. */}
              <div className="fonte-detalhe">vale na coleta seguinte</div>
            </div>
            <button
              className="botao"
              role="switch"
              aria-checked={filtro.ligado}
              disabled={alternandoFiltro || (!filtro.ligado && !podeSalvar)}
              title={filtro.ligado
                ? 'Desliga o recorte por data de faturamento: todo pedido emitido na janela passa a contar.'
                : 'Liga o recorte com as datas ao lado. Atenção: medido, isso descarta 94,7 % das linhas.'}
              /* Ligar leva as datas da tela na MESMA chamada: ligar primeiro e
                 corrigir a data depois deixaria um ciclo do coletor medindo pela
                 janela velha, e esse ciclo aparece no telão. */
              onClick={() => aoAlternarFiltroFaturamento(!filtro.ligado, de, ate)}
            >
              {alternandoFiltro ? 'Salvando…' : filtro.ligado ? 'Desligar' : 'Ligar'}
            </button>

            {/* Segunda linha do mesmo bloco: as datas que o filtro aplica.
                Ficam SEMPRE editáveis, inclusive com o filtro desligado — é
                assim que se deixa a janela pronta antes de ligar, com a sala
                cheia. */}
            <div className="filtro-datas">
              <label>
                Faturamento de
                <input
                  type="date" value={de} disabled={alternandoFiltro}
                  onChange={(evento) => setDe(evento.target.value)}
                />
              </label>
              <label>
                até
                <input
                  type="date" value={ate} disabled={alternandoFiltro}
                  onChange={(evento) => setAte(evento.target.value)}
                />
              </label>
              <button
                className="botao"
                disabled={!podeSalvar || !alterouDatas}
                title={alterouDatas
                  ? 'Grava as datas sem mexer no interruptor.'
                  : 'As datas são as que já estão gravadas.'}
                onClick={() => aoAlternarFiltroFaturamento(filtro.ligado, de, ate)}
              >
                Salvar datas
              </button>
              {invertida ? (
                <span className="erro">A data final é anterior à inicial.</span>
              ) : alterouDatas ? (
                <span className="fonte-detalhe">
                  Alterado e ainda não gravado{filtro.ligado ? '' : ' — o filtro continua desligado'}.
                </span>
              ) : null}
            </div>
          </div>
        )}
        {erroFiltro ? <div className="rodape-acoes"><span className="erro">{erroFiltro}</span></div> : null}
      </div>

      <h2>Fontes de dados</h2>
      <p className="ajuda">
        É por esta aba que se descobre, no meio do evento, que o número parou de subir.
        O coletor roda dentro da rede e envia o snapshot para o painel; o painel nunca
        consulta o ERP diretamente — então a recarga é um pedido ao coletor, não uma
        consulta daqui.
      </p>

      <div className="painel-admin">
        {/* Área em branco não informa nada: antes da primeira consulta, depois
            de uma falha e com a lista vazia a aba diz o que está acontecendo. */}
        {dados === null ? (
          <div className="ajuda">
            {erro ? 'Não foi possível consultar o estado das fontes.' : 'Consultando o estado das fontes…'}
          </div>
        ) : dados.fontes.length === 0 ? (
          <div className="ajuda">O painel não conhece nenhuma fonte — nada foi coletado ainda.</div>
        ) : dados.fontes.map(fonte => {
          const aparencia = APARENCIA[fonte.estado] ?? APARENCIA.indisponivel
          return (
            <div className="fonte" key={fonte.id}>
              <span className="ponto" style={{ background: aparencia.cor }} />
              <div>
                <div className="fonte-nome">
                  {fonte.nome} <span className="fonte-qualificador">· {fonte.descricao}</span>
                </div>
                <div className="fonte-detalhe">
                  {fonte.detalhe || 'sem detalhe informado'}
                  {fonte.ms === null ? '' : ` · leitura em ${fonte.ms} ms`}
                </div>
              </div>
              <div className="fonte-estado">
                <div className="selo" style={{ color: aparencia.cor }}>{aparencia.selo}</div>
                <div className="fonte-detalhe">{idadeDe(fonte.lidoEm, agora)}</div>
              </div>
              <button
                className="botao"
                disabled={aparencia.semRecarga || recarregando !== null}
                title={aparencia.semRecarga
                  ? 'Fonte nunca configurada: não há o que recarregar.'
                  : 'Pede ao coletor uma leitura nova desta fonte.'}
                onClick={() => aoRecarregar(fonte.id)}
              >
                {recarregando === fonte.id ? 'Pedindo…' : 'Recarregar'}
              </button>
            </div>
          )
        })}

        <div className="rodape-acoes">
          <button
            className="botao" disabled={recarregando !== null}
            onClick={() => aoRecarregar(null)}
          >
            Recarregar todas
          </button>
          <span className="fonte-detalhe">
            {/* Várias assinaturas inválidas seguidas é como se descobre que
                alguém está tentando, ou que o relógio do coletor saiu do lugar
                (a janela do snapshot é de 60 s). */}
            Rejeições de ingestão: <b>{dados?.rejeicoes ?? 0}</b>
            {motivos.length === 0
              ? ''
              : ` · ${motivos.map(([motivo, quantas]) => `${motivo}: ${quantas}`).join(' · ')}`}
          </span>
          {aviso ? <span className="fonte-detalhe">{aviso}</span> : null}
          {erro ? <span className="erro">{erro}</span> : null}
        </div>
        <div className="fonte-detalhe">
          Última coleta aceita: {dados?.coletadoEm ? idadeDe(dados.coletadoEm, agora) : 'nenhuma ainda'}
        </div>
      </div>
    </section>
  )
}
